import { useRef, useEffect, useState } from 'react';

const domains = [
  {
    id: 'fashion',
    label: '01',
    title: 'Fashion Brands',
    subtitle: 'Editorial & Campaign',
    description:
      'High-end photography for luxury fashion houses, emerging designers and premium brands. Editorial precision, Parisian light.',
    image:
      'https://static.readdy.ai/image/88800be73431afcf1237f8975cc2c2ab/d82018a541cd03ca72f0aa1debf59ee7.png',
    scrollTarget: 'offers-section',
  },
  {
    id: 'hotel',
    label: '02',
    title: 'Hotels',
    subtitle: 'Architecture & Interiors',
    description:
      'From boutique retreats to grand palaces — we capture the soul of each space, converting admiration into reservation.',
    image:
      'https://storage.readdy-site.link/project_files/ab967d20-2987-4048-a339-d91f8ceb0be3/c919663d-d598-4b19-9879-88c3a27df9b5_9.png?v=6f4601e3fbc986504b334a8dba4ddb95',
    scrollTarget: 'hotel-section',
  },
  {
    id: 'restaurant',
    label: '03',
    title: 'Restaurants',
    subtitle: 'Gastronomy & Ambiance',
    description:
      'Gastronomy photographed with intention — ambiance, plating details, the quiet elegance of a table well set.',
    image:
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200&q=85&fit=crop',
    scrollTarget: 'hospitality-section',
  },
];

interface DomainCardProps {
  domain: (typeof domains)[0];
  index: number;
  visible: boolean;
}

function DomainCard({ domain, index, visible }: DomainCardProps) {
  const handleClick = () => {
    document.getElementById(domain.scrollTarget)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className="relative group overflow-hidden cursor-pointer"
      style={{
        height: '620px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.8s ease ${index * 0.18}s, transform 0.8s ease ${index * 0.18}s`,
      }}
      onClick={handleClick}
    >
      {/* Image background */}
      <img
        src={domain.image}
        alt={domain.title}
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10 transition-all duration-500 group-hover:from-black/90 group-hover:via-black/45"></div>

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10">
        <span className="font-['Montserrat'] text-xs text-white/50 tracking-[0.3em] mb-3 block">
          {domain.label}
        </span>
        <h3 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light text-white mb-1 leading-tight">
          {domain.title}
        </h3>
        <p className="font-['Montserrat'] text-xs text-white/60 uppercase tracking-widest mb-5">
          {domain.subtitle}
        </p>

        {/* Description — revealed on hover */}
        <p className="font-serif text-sm text-white/80 leading-relaxed max-w-xs mb-6 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-500">
          {domain.description}
        </p>

        <div className="flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <span className="font-['Montserrat'] text-xs text-white/80 uppercase tracking-wider">
            Discover
          </span>
          <div className="w-8 h-px bg-white/60 group-hover:w-14 transition-all duration-500"></div>
        </div>
      </div>

      {/* Top label */}
      <div className="absolute top-8 left-8">
        <div className="w-8 h-px bg-white/40 mb-2"></div>
      </div>
    </div>
  );
}

export function DomainsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28 px-4 md:px-6">
      {/* Header */}
      <div className="text-center mb-14 md:mb-20">
        <p className="font-['Montserrat'] text-xs uppercase tracking-[0.35em] text-gray-400 mb-5">
          Our Fields of Excellence
        </p>
        <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-6xl font-light text-gray-900 mb-5 leading-tight">
          Three Domains,<br />One Visual Language
        </h2>
        <div className="w-px h-12 bg-gray-300 mx-auto mt-4"></div>
      </div>

      {/* 3 columns */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {domains.map((domain, index) => (
          <DomainCard
            key={domain.id}
            domain={domain}
            index={index}
            visible={visible}
          />
        ))}
      </div>

      {/* Bottom note */}
      <div className="text-center mt-14">
        <p className="font-serif italic text-sm text-gray-400 max-w-xl mx-auto">
          Every image is composed with the same precision — whether for a runway collection, a five-star suite, or a Michelin-starred plate.
        </p>
      </div>
    </section>
  );
}
